import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
    Brain, Shield, AlertTriangle, FileText, Send, Loader2,
    Trash2, ChevronLeft, Sparkles, Bot
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGENTS = {
    aura: {
        id: 'aura', name: 'AURA', subtitle: 'Asistente de Operaciones',
        icon: Brain, color: 'sky',
        desc: 'Consultas sobre vuelos, flota, tripulacion, reservaciones y estadisticas operacionales.',
        roles: ['admin', 'ccod', 'dispatcher', 'gerencia', 'analista', 'comercial', 'crew_admin'],
        suggestions: ['Resumen del vuelo TGU444', 'Estado actual de la flota', 'Cuantos pasajeros tenemos hoy?']
    },
    copiloto: {
        id: 'copiloto', name: 'COPILOTO', subtitle: 'Asistente de Tripulacion',
        icon: Shield, color: 'emerald',
        desc: 'Horas de vuelo, FDTL, bitacora, regulaciones FAA/OACI y reportes del capitan.',
        roles: ['pilot', 'crew_admin', 'admin', 'ccm'],
        suggestions: ['Cuantas horas de vuelo llevo este mes?', 'Verificar limites FDTL', 'Ultimo reporte del capitan']
    },
    watchdog: {
        id: 'watchdog', name: 'WATCHDOG', subtitle: 'Monitor de Alertas',
        icon: AlertTriangle, color: 'amber',
        desc: 'Deteccion de conflictos, diferidos vencidos, alertas de seguridad y anomalias operacionales.',
        roles: ['admin', 'ccod', 'dispatcher', 'maintenance', 'gerencia'],
        suggestions: ['Hay alertas activas?', 'Estado de diferidos MEL', 'Revisar conflictos de programacion']
    },
    scribe: {
        id: 'scribe', name: 'SCRIBE', subtitle: 'Generador de Reportes',
        icon: FileText, color: 'violet',
        desc: 'Genera reportes ejecutivos, resumenes operacionales y analisis de datos.',
        roles: ['admin', 'gerencia', 'analista', 'ccod'],
        suggestions: ['Reporte diario de operaciones', 'Analisis de load factor por ruta', 'Resumen de seguridad SMS']
    },
};

const colorMap = {
    sky: { bg: 'bg-sky-500/10', text: 'text-sky-500', border: 'border-sky-500/20', accent: '#0ea5e9' },
    emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-500', border: 'border-emerald-500/20', accent: '#10b981' },
    amber: { bg: 'bg-amber-500/10', text: 'text-amber-500', border: 'border-amber-500/20', accent: '#f59e0b' },
    violet: { bg: 'bg-violet-500/10', text: 'text-violet-500', border: 'border-violet-500/20', accent: '#8b5cf6' },
};

function AgentSelector({ agents, onSelect }) {
    return (
        <div className="space-y-6 max-w-4xl mx-auto py-6" data-testid="agent-selector">
            <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold tracking-widest uppercase mb-4">
                    <Sparkles className="w-3.5 h-3.5" /> Agentes IA
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase font-['Barlow_Condensed']" data-testid="ai-agents-title">
                    Asistentes Inteligentes
                </h1>
                <p className="text-sm text-muted-foreground mt-2">Selecciona un agente para iniciar una conversacion.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
                {agents.map((agent) => {
                    const Icon = agent.icon;
                    const colors = colorMap[agent.color];
                    return (
                        <Card
                            key={agent.id}
                            className="card-aviation cursor-pointer group hover:shadow-lg transition-all duration-200"
                            onClick={() => onSelect(agent.id)}
                            data-testid={`agent-card-${agent.id}`}
                        >
                            <CardContent className="p-5">
                                <div className="flex items-start gap-4">
                                    <div className={`w-12 h-12 rounded-xl ${colors.bg} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-200`}>
                                        <Icon className={`w-6 h-6 ${colors.text}`} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-bold text-base font-['Barlow_Condensed'] uppercase tracking-wide">{agent.name}</h3>
                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${colors.bg} ${colors.text}`}>{agent.subtitle}</span>
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{agent.desc}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>
        </div>
    );
}

function ChatInterface({ agent, onBack }) {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const chatEndRef = useRef(null);
    const Icon = agent.icon;
    const colors = colorMap[agent.color];

    useEffect(() => {
        const loadHistory = async () => {
            try {
                const res = await axios.get(`${API}/ai/${agent.id}/history`);
                const history = res.data || [];
                setMessages(history.map(h => ({ role: h.role, content: h.content })));
            } catch { /* ignore */ }
            setHistoryLoaded(true);
        };
        loadHistory();
    }, [agent.id]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const sendMessage = async (text) => {
        const msg = text || input.trim();
        if (!msg || loading) return;
        setInput('');
        setMessages(prev => [...prev, { role: 'user', content: msg }]);
        setLoading(true);
        try {
            const res = await axios.post(`${API}/ai/${agent.id}/chat`, { message: msg });
            setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
        } catch (err) {
            const detail = err.response?.data?.detail || 'Error al comunicarse con el agente';
            toast.error(detail);
            setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${detail}` }]);
        } finally {
            setLoading(false);
        }
    };

    const clearHistory = async () => {
        try {
            await axios.delete(`${API}/ai/${agent.id}/history`);
            setMessages([]);
            toast.success('Historial limpiado');
        } catch {
            toast.error('Error al limpiar historial');
        }
    };

    const handleSubmit = (e) => { e.preventDefault(); sendMessage(); };

    return (
        <div className="flex flex-col h-[calc(100vh-8rem)] max-w-4xl mx-auto" data-testid={`chat-${agent.id}`}>
            {/* Header */}
            <div className="flex items-center justify-between pb-4 border-b border-border/30">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={onBack} data-testid="chat-back-btn">
                        <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <div className={`w-10 h-10 rounded-xl ${colors.bg} flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 ${colors.text}`} />
                    </div>
                    <div>
                        <h2 className="font-bold text-base font-['Barlow_Condensed'] uppercase tracking-wide">{agent.name}</h2>
                        <p className="text-xs text-muted-foreground">{agent.subtitle}</p>
                    </div>
                </div>
                <Button variant="ghost" size="sm" onClick={clearHistory} className="text-muted-foreground hover:text-destructive" data-testid="chat-clear-btn">
                    <Trash2 className="w-4 h-4" />
                </Button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto py-4 space-y-4 min-h-0">
                {messages.length === 0 && historyLoaded && (
                    <div className="text-center py-12">
                        <div className={`w-16 h-16 rounded-2xl ${colors.bg} flex items-center justify-center mx-auto mb-4`}>
                            <Icon className={`w-8 h-8 ${colors.text}`} />
                        </div>
                        <h3 className="font-bold font-['Barlow_Condensed'] uppercase text-lg">{agent.name}</h3>
                        <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">{agent.desc}</p>
                        {agent.suggestions && (
                            <div className="mt-6 flex flex-wrap justify-center gap-2">
                                {agent.suggestions.map((s) => (
                                    <Button key={s} variant="outline" size="sm" onClick={() => sendMessage(s)}
                                        className="text-xs" data-testid={`suggestion-btn`}>
                                        {s}
                                    </Button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
                {messages.map((msg, i) => (
                    <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${i}`}>
                        {msg.role === 'assistant' && (
                            <div className={`w-8 h-8 rounded-lg ${colors.bg} flex items-center justify-center flex-shrink-0 mt-1`}>
                                <Bot className={`w-4 h-4 ${colors.text}`} />
                            </div>
                        )}
                        <div className={`max-w-[80%] rounded-xl px-4 py-3 text-sm ${
                            msg.role === 'user'
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted/50 border border-border/30'
                        }`}>
                            {msg.role === 'assistant' ? (
                                <div className="prose prose-sm dark:prose-invert max-w-none [&>p]:my-1 [&>ul]:my-1 [&>ol]:my-1 [&>table]:text-xs">
                                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                                </div>
                            ) : (
                                <p>{msg.content}</p>
                            )}
                        </div>
                    </div>
                ))}
                {loading && (
                    <div className="flex gap-3" data-testid="chat-loading">
                        <div className={`w-8 h-8 rounded-lg ${colors.bg} flex items-center justify-center`}>
                            <Bot className={`w-4 h-4 ${colors.text}`} />
                        </div>
                        <div className="bg-muted/50 border border-border/30 rounded-xl px-4 py-3">
                            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                        </div>
                    </div>
                )}
                <div ref={chatEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={handleSubmit} className="flex gap-2 pt-4 border-t border-border/30" data-testid="chat-input-form">
                <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={`Preguntale a ${agent.name}...`}
                    disabled={loading}
                    className="input-aviation flex-1"
                    data-testid="chat-input"
                />
                <Button type="submit" disabled={loading || !input.trim()} className="btn-primary px-4" data-testid="chat-send-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
            </form>
        </div>
    );
}

export default function AIAgentsPage() {
    const { user } = useAuth();
    const [selectedAgent, setSelectedAgent] = useState(null);

    const availableAgents = Object.values(AGENTS).filter(a => a.roles.includes(user?.role));

    if (selectedAgent) {
        return <ChatInterface agent={AGENTS[selectedAgent]} onBack={() => setSelectedAgent(null)} />;
    }

    return <AgentSelector agents={availableAgents} onSelect={setSelectedAgent} />;
}
